/** Shotstack Edit API default output frame rate. */
export const SHOTSTACK_FPS = 25;

/** Shortest playable trim the renderer will accept. */
export const MIN_TRIM_SECONDS = 0.2;

export const EDIT_STATE_VERSION = 1 as const;

export type ReworkStatus = 'queued' | 'processing' | 'completed' | 'failed';

export interface ClipVersion {
  id: string;
  clipUrl: string;
  clipPath: string | null;
  duration: number;
  createdAt: string;
  instructions?: string | null;
  generationId?: string | null;
}

export interface PendingRework {
  generationId: string | null;
  instructions: string;
  startedAt: string;
  status: ReworkStatus;
  clipUrl?: string | null;
  clipPath?: string | null;
  duration?: number | null;
  error?: string | null;
}

export interface ClipEdit {
  id: string;
  order: number;
  included: boolean;
  trimStart: number;
  trimEnd: number;
  sourceDuration: number;
  activeVersionId: string;
  versions: ClipVersion[];
  pendingRework?: PendingRework | null;
}

export interface ProjectEditState {
  version: typeof EDIT_STATE_VERSION;
  revision: number;
  updatedAt: string;
  clips: ClipEdit[];
}

export interface SourceClipImage {
  id: string;
  clip_url: string | null;
  clip_path?: string | null;
  clip_duration: number;
  order_index: number;
  pending_clip_url?: string | null;
  pending_clip_path?: string | null;
  pending_clip_duration?: number | null;
  pending_generation_id?: string | null;
  clip_versions?: ClipVersion[] | null;
}

export interface RenderClipInput {
  id: string;
  clipUrl: string;
  clipPath?: string | null;
  sourceDuration: number;
  trimStart: number;
  trimEnd: number;
  order: number;
  versionId?: string;
}

export interface RenderSnapshot {
  revision: number;
  capturedAt: string;
  clips: RenderClipInput[];
}

export type ReconstructFailureReason = 'no_clips' | 'final_only' | 'invalid';

export interface ReconstructSuccess {
  ok: true;
  state: ProjectEditState;
  synthesized: boolean;
}

export interface ReconstructFailure {
  ok: false;
  reason: ReconstructFailureReason;
  message: string;
}

export type ReconstructResult = ReconstructSuccess | ReconstructFailure;

export interface ProjectSettingsLike {
  transition_effect?: string;
  aspect_ratio?: '16:9' | '9:16' | '4:3' | '3:4' | '1:1';
  output_resolution?: 'hd';
  clip_duration?: number;
  filter?: string | null;
  intro_text?: string | null;
  outro_text?: string | null;
  title_font_size?: number;
  logo_on_title_cards?: boolean;
  logo_intro_card?: boolean;
  logo_outro_card?: boolean;
  logo_bumper_duration?: number;
  logo_bumper_tagline?: string | null;
  logo_bumper_animation?: string;
  logo_position?: string;
  logo_scale?: number;
  logo_opacity?: number;
  music_volume?: number;
  music_effect?: string;
  background_color?: string;
}

export interface TimelineBuildResult {
  timeline: {
    background: string;
    tracks: Array<{ clips: Record<string, unknown>[] }>;
  };
  output: {
    format: 'mp4';
    resolution: 'hd';
    aspectRatio: string;
    size: { width: number; height: number };
  };
  totalDuration: number;
  includedClipCount: number;
}
